import React, { useEffect, useState } from "react";
import { Input, Dropdown, Spin } from "antd";

import {
  SearchOutlined,
  EnvironmentOutlined,
  AimOutlined,
} from "@ant-design/icons";

import { useNavigate } from "react-router-dom";
import { useCity } from "../context/CityContext";

import {
  getCities,
  getSearchProducts,
} from "../api/searchApi";

const SearchDropdown = ({ size }) => {
  // ==========================================
  // HOOKS
  // ==========================================

  const navigate = useNavigate();
  const { selectCity } = useCity();

  // ==========================================
  // STATE
  // ==========================================

  const [cities, setCities] = useState([]);
  const [products, setProducts] = useState([]);

  const [filteredCities, setFilteredCities] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);

  const [recentSearches, setRecentSearches] = useState([]);

  const [searchValue, setSearchValue] = useState("");

  const [loading, setLoading] = useState(false);

  const [open, setOpen] = useState(false);

  // ==========================================
  // INITIAL LOAD
  // ==========================================

  useEffect(() => {
    fetchSearchData();
    loadRecentSearches();
  }, []);

  // ==========================================
  // FETCH CITY + PRODUCT
  // ==========================================

  const fetchSearchData = async () => {
    try {
      setLoading(true);

      const [cityResponse, productResponse] =
        await Promise.all([
          getCities(),
          getSearchProducts(),
        ]);

 

      // ========================================
      // FORMAT CITY DATA
      // ========================================

      const cityList = cityResponse.map((city) => ({
        id: city.id,

        type: "city",

        name: city.name || "",

        slug: city.slug || "",

        countryName:
          city.country?.name || "",

        countrySlug:
          city.country?.slug || "",
      }));

      

      setCities(cityList);
      setFilteredCities(cityList);

      // ========================================
      // FORMAT PRODUCT DATA
      // ========================================

      const productList = productResponse.map(
        (details) => {
          const city = details.city || {};
          const country = city.country || {};

          const subProducts =
            details.sub_product?.products || [];

          return {
            id: details.id,

            type: "product",

            title: details.title || "",

            slug: details.slug || "",

            // ------------------------------
            // CITY
            // ------------------------------

            cityName: city.name || "",

            citySlug: city.slug || "",

            // ------------------------------
            // COUNTRY
            // ------------------------------

            countryName: country.name || "",

            countrySlug: country.slug || "",

            // ------------------------------
            // SUB PRODUCT
            // ------------------------------

            subProductSlug:
              details.sub_product?.slug || "",

            productSlugs:
              subProducts
                .map((product) => product.slug)
                .filter(Boolean),

            // ------------------------------
            // KEYWORDS
            // ------------------------------

            keywords:
              details.keywords
                ?.map((keyword) =>
                  keyword.value?.toLowerCase()
                )
                .filter(Boolean) || [],

            // ------------------------------
            // IMAGE
            // ------------------------------

            image:
              details.images?.[0]?.url
                ? `${process.env.REACT_APP_API_URL}${details.images[0].url}`
                : null,
          };
        }
      );

 

      setProducts(productList);
      setFilteredProducts(productList);

    } catch (error) {
      console.error(
        "Search API Error:",
        error
      );
    } finally {
      setLoading(false);
    }
  };

  // ==========================================
  // LOAD RECENT SEARCHES
  // ==========================================

  const loadRecentSearches = () => {
    try {
      const stored =
        localStorage.getItem(
          "recentSearches"
        );

      if (!stored) return;

      const parsed = JSON.parse(stored);

      if (Array.isArray(parsed)) {
        setRecentSearches(parsed);
      }

    } catch (error) {
      console.error(
        "Recent search error:",
        error
      );
    }
  };

  // ==========================================
  // SAVE RECENT SEARCH
  // ==========================================

  const saveRecentSearch = (item) => {
    const existing =
      recentSearches.filter(
        (recent) =>
          !(
            recent.type === item.type &&
            recent.slug === item.slug
          )
      );

    const updated = [
      item,
      ...existing,
    ].slice(0, 5);

    setRecentSearches(updated);

    localStorage.setItem(
      "recentSearches",
      JSON.stringify(updated)
    );
  };

  // ==========================================
  // HANDLE SEARCH
  // ==========================================

  const handleSearch = (value) => {
    setSearchValue(value);

    const search = value.trim().toLowerCase();

    // ========================================
    // EMPTY SEARCH
    // ========================================

    if (!search) {
      // Show initial results when input is clicked
      setFilteredCities(cities.slice(0, 5));
      setFilteredProducts(products.slice(0, 5));
      return;
    }

    // ========================================
    // CITY SEARCH
    // ========================================

    const cityResults = cities.filter((city) => {
      const cityName =
        city.name?.toLowerCase() || "";

      const countryName =
        city.countryName?.toLowerCase() || "";

      const citySlug =
        city.slug?.toLowerCase() || "";

      const countrySlug =
        city.countrySlug?.toLowerCase() || "";

      return (
        cityName.includes(search) ||
        countryName.includes(search) ||
        citySlug.includes(search) ||
        countrySlug.includes(search)
      );
    });

    // ========================================
    // PRODUCT SEARCH
    // ========================================

    const matchedCityIds = new Set(
      cityResults.map((city) => city.id)
    );

    const productResults = products.filter((product) => {
      const title =
        product.title?.toLowerCase() || "";

      const cityName =
        product.cityName?.toLowerCase() || "";

      const countryName =
        product.countryName?.toLowerCase() || "";

      const citySlug =
        product.citySlug?.toLowerCase() || "";

      const countrySlug =
        product.countrySlug?.toLowerCase() || "";

      const keywordMatch =
        product.keywords?.some((keyword) =>
          keyword.includes(search)
        );

      const relatedCity =
        matchedCityIds.has(product.cityId);

      return (
        title.includes(search) ||
        cityName.includes(search) ||
        countryName.includes(search) ||
        citySlug.includes(search) ||
        countrySlug.includes(search) ||
        keywordMatch ||
        relatedCity
      );
    });

    setFilteredCities(cityResults);
    setFilteredProducts(productResults);
  };

  // ==========================================
  // CITY CLICK
  // ==========================================

  const handleCityClick = (city) => {
    saveRecentSearch({
      id: city.id,

      name: city.name,

      slug: city.slug,

      type: "city",

      countryName:
        city.countryName,
    });

    setSearchValue(city.name);

    setOpen(false);

    selectCity(city);

    navigate(
      `/city/${city.slug}`
    );
  };

  // ==========================================
  // PRODUCT CLICK
  // ==========================================

  const handleProductClick = (product) => {
    saveRecentSearch({
      id: product.id,

      name: product.title,

      slug: product.slug,

      type: "product",

      image: product.image,

      subProductSlug:
        product.subProductSlug,
    });

    setOpen(false);

    navigate(
      `/${product.subProductSlug}/${product.slug}`
    );
  };

  // ==========================================
  // RECENT SEARCH CLICK
  // ==========================================

  const handleRecentClick = (item) => {
    if (item.type === "city") {
      handleCityClick(item);
    } else {
      handleProductClick(item);
    }
  };

  // ==========================================
  // DROPDOWN CONTENT
  // ==========================================

  const dropdownContent = (
    <div className="bg-white rounded-2xl shadow-xl w-full md:w-[550px] max-h-[400px] overflow-y-auto p-4">

      {/* ================================= */}
      {/* LOADING */}
      {/* ================================= */}

      {loading && (
        <div className="flex justify-center py-8">
          <Spin />
        </div>
      )}

      {!loading && (
        <>
          {/* ============================== */}
          {/* DESTINATIONS */}
          {/* ============================== */}

          {filteredCities.length > 0 && (
            <div>
              <div className="text-xs font-semibold text-gray-400 uppercase px-2 mb-2">
                Destinations
              </div>

              {filteredCities.map(
                (city) => (
                  <div
                    key={city.id}
                    className="flex items-center gap-2 py-2 px-2 rounded-xl hover:bg-gray-100 cursor-pointer"
                    onClick={() =>
                      handleCityClick(city)
                    }
                  >
                    <div className="text-gray-500 text-xl">
                      <AimOutlined />
                    </div>

                    <div>
                      <div className="font-medium text-gray-800 text-sm truncate">
                        {city.name}
                      </div>

                      <div className="text-xs text-gray-500">
                        {city.countryName}
                      </div>
                    </div>
                  </div>
                )
              )}
            </div>
          )}

          {/* ============================== */}
          {/* EXPERIENCES */}
          {/* ============================== */}

          {filteredProducts.length > 0 && (
            <div className="mt-3">
              <div className="text-xs font-semibold text-gray-400 uppercase px-2 mb-2">
                Experiences
              </div>

              {filteredProducts.map(
                (product) => (
                  <div
                    key={product.id}
                    className="flex items-center gap-3 py-2 px-2 rounded-xl hover:bg-gray-100 cursor-pointer"
                    onClick={() =>
                      handleProductClick(
                        product
                      )
                    }
                  >
                    {/* IMAGE */}

                    {product.image ? (
                      <img
                        src={product.image}
                        alt={product.title}
                        className="w-10 h-10 rounded-lg object-cover"
                      />
                    ) : (
                      <div className="text-gray-500 text-xl w-12 flex justify-center">
                        <EnvironmentOutlined />
                      </div>
                    )}

                    {/* PRODUCT INFO */}

                    <div className="flex-1 min-w-0">

                      <div className="font-medium text-gray-800 text-sm truncate">
                        {product.title}
                      </div>

                      <div className="text-xs text-gray-500">
                        {product.cityName}

                        {product.cityName &&
                          product.countryName &&
                          " · "}

                        {product.countryName}
                      </div>

                    </div>
                  </div>
                )
              )}
            </div>
          )}

          {/* ============================== */}
          {/* NO RESULTS */}
          {/* ============================== */}

          {filteredCities.length === 0 &&
            filteredProducts.length === 0 &&
            searchValue.trim() && (
              <div className="py-8 text-center text-gray-500">
                No destinations or experiences found
              </div>
            )}
        </>
      )}
    </div>
  );

  // ==========================================
  // RETURN
  // ==========================================

  return (
    <div className="relative z-[9999] w-full md:w-[550px] mx-auto">

      <Dropdown
        dropdownRender={() =>
          dropdownContent
        }
        trigger={["click"]}
        open={open}
        onOpenChange={(flag) =>
          setOpen(flag)
        }
        placement="bottomLeft"
      >

        <Input
          size="large"
          placeholder="Search for Destination"
          value={searchValue}
          onChange={(e) =>
            handleSearch(
              e.target.value
            )
          }
          suffix={
            <SearchOutlined className="text-xl" />
          }
          className={`
            ${size || "h-16"}
            w-full
            rounded-[100px]
            font-normal
            px-6
            text-base
            bg-white
            border-0
            shadow-sm
          `}
          onClick={() => {
            setOpen(true);

            // Show initial results immediately
            if (!searchValue.trim()) {
              setFilteredCities(cities.slice(0, 5));
              setFilteredProducts(products.slice(0, 5));
            }
          }}
        />

      </Dropdown>
    </div>
  );
};

export default SearchDropdown;